import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { createPortal } from "react-dom";
import { StopPrice } from "./trip-planner/stop-price";
import { stopBookingKind } from "../lib/trip-booking";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LocationMap } from "@majourneys/booking/location-map";
import { useItemPrice } from "../config";
import type { DiscoverItem } from "../types";
import { useFocusTrap } from "../hooks/use-focus-trap";
import {
  useDiscoverActions,
  useDiscoverContext,
} from "../state/discover-context";
import { useTripActions, useTripContext } from "../state/trip-context";
import { useActiveTrip } from "../hooks/use-active-trip";
import { useDiscoverDetail } from "../hooks/use-discover-detail";
// Legacy MDX content has been removed; these stubs keep the slot rendering
// logic intact while returning no content from the old static seed.
const casablancaListingBodies: Record<string, ComponentType | undefined> = {};
const casablancaListingSections: Record<string, readonly { title: string; body: string }[]> = {};
import {
  templateRegistry,
  EXPLORE_TEMPLATE_BY_SEGMENT,
} from "@majourneys/shared";
import { DiscoverContentBlocks } from "./discover-content-blocks";
import { pickLocale } from "../lib/content-blocks";
import { thumbUrl } from "./map/thumb-url";

import { Icon } from "@majourneys/icons";
function stripHtml(html: string): string {
  if (!html) return "";
  const noScripts = html.replace(/<script[\s\S]*?<\/script>/gi, " ").replace(/<style[\s\S]*?<\/style>/gi, " ");
  return noScripts.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function looksLikeHtml(value: string): boolean {
  return /<\/?(p|div|h[1-6]|ul|ol|li|strong|em|br|span|a)\b/i.test(value);
}

function hasAnyContentBlocks(item: DiscoverItem): boolean {
  const blocks = item.contentBlocks;
  if (!blocks) return false;
  const templateId = item.template ?? EXPLORE_TEMPLATE_BY_SEGMENT[item.segment];
  const def = templateId ? templateRegistry[templateId] : null;
  if (!def) return false;
  return def.blocks.some((b) => {
    const v = blocks[b.key];
    if (!v) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") {
      const o = v as Record<string, unknown>;
      return Object.values(o).some((arr) => Array.isArray(arr) && arr.length > 0);
    }
    return false;
  });
}

function matchFact(facts: readonly string[], keywords: readonly string[]): string | null {
  for (const fact of facts) {
    const f = fact.toLowerCase();
    if (keywords.some((k) => f.includes(k))) return fact;
  }
  return null;
}

interface FactBlock {
  readonly label: string;
  readonly value: string;
}

function buildGoodToKnow(item: DiscoverItem, price: string | null): readonly FactBlock[] {
  // `label` holds an i18n key (resolved at render) so the Good-to-know grid
  // localizes across all 7 locales.
  const blocks: FactBlock[] = [];
  blocks.push({ label: "discover.gtk.category", value: item.subtitle });
  if (item.mode === "bookable" && price) {
    blocks.push({ label: "discover.gtk.price", value: price });
  }

  const bestTime = matchFact(item.facts, [
    "sunset",
    "morning",
    "evening",
    "daylight",
    "weekday",
    "summer",
  ]);
  if (bestTime) blocks.push({ label: "discover.gtk.best_time", value: bestTime });

  const duration = matchFact(item.facts, [
    "half-day",
    "full-day",
    "hour",
    "minute",
  ]);
  if (duration) blocks.push({ label: "discover.gtk.duration", value: duration });

  const access = matchFact(item.facts, [
    "free",
    "ticket",
    "reservation",
    "book",
    "open",
  ]);
  if (access && access !== bestTime && access !== duration)
    blocks.push({ label: "discover.gtk.access", value: access });

  for (const fact of item.facts) {
    if (fact === bestTime || fact === duration || fact === access) continue;
    if (blocks.some((b) => b.value === fact)) continue;
    if (blocks.length >= 6) break;
    blocks.push({ label: "discover.gtk.note", value: fact });
  }
  return blocks;
}

type DetailTabId = "overview" | "location" | "faq";

const DETAIL_TABS: readonly { id: DetailTabId; labelKey: string }[] = [
  { id: "overview", labelKey: "activity.overview" },
  { id: "location", labelKey: "activity.location" },
  { id: "faq", labelKey: "common.faqs" },
];

export function DetailSlideOver({ item: rawItem, open }: { readonly item: DiscoverItem | null; readonly open: boolean }) {
  const { t } = useTranslation();
  const itemPrice = useItemPrice();
  const { closeDetail, toggleLiked, setUtilityMode } = useDiscoverActions();
  const { state } = useDiscoverContext();
  const { state: tripState } = useTripContext();
  const { addStop } = useTripActions();
  const activeTrip = useActiveTrip();
  const params = useParams({ strict: false }) as { lang?: string };
  const lang = params.lang ?? "en";
  // Lazy-load heavy editorial fields once a slide-over opens. Index payload
  // ships only card/map fields; this fills gallery/contentBlocks/facts/faqs/
  // overviewSections/subtitle on demand.
  const { data: detail } = useDiscoverDetail(rawItem?.sourceSlug ?? null, lang);
  const item: DiscoverItem | null = useMemo(() => {
    if (!rawItem) return null;
    if (!detail) return rawItem;
    const mergedGallery =
      detail.gallery.length > 0 ? detail.gallery : rawItem.gallery;
    return {
      ...rawItem,
      subtitle: detail.subtitle || rawItem.subtitle,
      headerDescription: detail.overview || rawItem.headerDescription,
      gallery: mergedGallery,
      facts: detail.facts,
      faqs: detail.faqs,
      overviewSections: detail.overviewSections,
      template: detail.template ?? rawItem.template ?? null,
      contentBlocks: detail.contentBlocks,
    };
  }, [rawItem, detail]);
  const [activeTab, setActiveTab] = useState<DetailTabId>("overview");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  const [brokenSrcs, setBrokenSrcs] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const panelRef = useRef<HTMLElement>(null);
  // Move focus into the panel on open, trap Tab, restore to the trigger on
  // close. Disabled while the lightbox (its own modal) is open so it doesn't
  // pull focus back out of the lightbox.
  useFocusTrap(panelRef, open && lightboxIndex === null);
  useEffect(() => setMounted(true), []);
  // Publish the open state to the root (same pattern as data-mj-discover-sheet)
  // so the mobile floating rail can hide itself while the detail panel covers
  // the screen — otherwise it floats over the Overview text.
  useEffect(() => {
    document.documentElement.dataset.mjDiscoverDetail = open ? "open" : "closed";
    return () => {
      delete document.documentElement.dataset.mjDiscoverDetail;
    };
  }, [open]);
  useEffect(() => {
    setBrokenSrcs(new Set());
  }, [item?.id]);

  const markBroken = (src: string) =>
    setBrokenSrcs((prev) => {
      if (prev.has(src)) return prev;
      const next = new Set(prev);
      next.add(src);
      return next;
    });

  const galleryImages = useMemo(() => {
    if (!item) return [] as readonly string[];
    // The panel previews 3 (hero + 2 thumbs), but the lightbox swipes through
    // the FULL gallery — so keep every image here.
    const all = item.gallery.filter((s): s is string => Boolean(s));
    if (all.length > 0) return all;
    return item.image ? [item.image] : [];
  }, [item]);

  const heroSrc = useMemo(() => {
    if (!item) return null;
    const candidates = [
      item.gallery[0],
      item.gallery[1],
      item.gallery[2],
      item.image,
    ].filter((s): s is string => Boolean(s));
    return candidates.find((s) => !brokenSrcs.has(s)) ?? null;
  }, [item, brokenSrcs]);

  const galleryAlt = (n: number) =>
    item?.galleryAlts?.[n] ?? item?.imageAlt ?? item?.title ?? "";

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight")
        setLightboxIndex((i) =>
          i === null ? null : (i + 1) % galleryImages.length,
        );
      if (e.key === "ArrowLeft")
        setLightboxIndex((i) =>
          i === null
            ? null
            : (i - 1 + galleryImages.length) % galleryImages.length,
        );
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, galleryImages.length]);

  const isLiked = item ? state.likedIds.has(item.id) : false;

  const inActiveTrip = useMemo(() => {
    if (!item || !activeTrip) return false;
    return activeTrip.days.some((day) =>
      day.stops.some((stop) => stop.itemId === item.id),
    );
  }, [item, activeTrip]);

  const handleAddToTrip = () => {
    if (!item) return;
    // No trips at all: send the user to trips mode to create one.
    if (tripState.trips.length === 0) {
      setUtilityMode("trips");
      closeDetail();
      return;
    }
    // No active trip selected: switch to trips mode so they can pick one.
    if (!activeTrip) {
      setUtilityMode("trips");
      closeDetail();
      return;
    }
    // Already in the active trip — don't double-add.
    if (inActiveTrip) return;
    // Drop the item into the last day of the active trip. When the
    // trip has no days yet we'd skip this, but createTrip always
    // seeds at least one day.
    const lastDay = activeTrip.days[activeTrip.days.length - 1];
    if (!lastDay) return;
    addStop(activeTrip.id, lastDay.id, item.id);
  };

  const addToTripLabel =
    tripState.trips.length === 0
      ? t("discover.add_to_trip")
      : !activeTrip
        ? t("discover.pick_a_trip")
        : inActiveTrip
          ? t("discover.in_trip")
          : t("discover.add_to_trip");

  // The way to the place's own page: booking for a bookable place, the page
  // for one we only describe. In the toolbar on a wide screen, in the
  // floating bar on a phone.
  const bookLink = (className: string) =>
    item?.sourceSlug ? (<Link
                    to={
                      item.mode === "bookable"
                        ? "/$lang/$city/$category/$slug/packages"
                        : "/$lang/$city/$category/$slug"
                    }
                    params={{
                      lang,
                      city: item.citySlug?.trim() || "morocco",
                      category:
                        item.categorySlug?.trim() ||
                        item.segment?.trim() ||
                        "place",
                      slug: item.sourceSlug,
                    }}
                    className={className}
                    aria-label={
                      item.mode === "bookable"
                        ? t("discover.book_item", { title: item.title })
                        : t("discover.open_item", { title: item.title })
                    }
                  >
                    <Icon name={item.mode === "bookable" ? "ri-ticket-2-line" : "ri-arrow-right-up-line"} aria-hidden="true" />
                    <span>{item.mode === "bookable" ? t("activity.book_now") : t("discover.open_page")}</span>
                  </Link>) : null;
  const bookKind = stopBookingKind(item);

  // Save, directions, add to trip. In the toolbar on a wide screen; on a
  // phone they move under the name as labelled buttons, because three bare
  // round icons beside "Book now" said nothing (owner, 2026-11-03).
  const secondaryActions = item ? (
    <>
                <button
                  type="button"
                  className={`gd-btn-action${isLiked ? " is-active" : ""}`}
                  onClick={() => toggleLiked(item.id)}
                  aria-pressed={isLiked}
                >
                  <Icon name={isLiked ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true" />
                  <span>{isLiked ? t("activity.saved") : t("activity.save")}</span>
                </button>
                {typeof item.latitude === "number" &&
                  typeof item.longitude === "number" && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${item.latitude},${item.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="gd-btn-action"
                      aria-label={t("discover.directions")}
                    >
                      <Icon name="ri-navigation-line" aria-hidden="true" />
                      <span>{t("discover.directions")}</span>
                    </a>
                  )}
                <button
                  type="button"
                  className={`gd-btn-action${inActiveTrip ? " is-active" : ""}`}
                  onClick={handleAddToTrip}
                  disabled={inActiveTrip}
                  aria-pressed={inActiveTrip}
                >
                  <Icon name={inActiveTrip ? "ri-check-line" : "ri-add-line"} aria-hidden="true" />
                  <span>{addToTripLabel}</span>
                </button>
    </>
  ) : null;

  return (
    <aside
      ref={panelRef}
      className={`mj-discover-detail${open ? " is-open" : ""}`}
      aria-hidden={!open}
      aria-label={item ? t("discover.details_for", { title: item.title }) : t("discover.details_panel")}
    >
      {item && (
        <div className="gd-panel">
          {/* Phone sheet: the handle to pull it up or down (the sheet's
              wrapper reads the drag). Hidden on a wide screen. */}
          <div className="gd-grab" aria-hidden="true">
            <span />
          </div>
          <div className="gd-chrome">
            <div className="gd-toolbar">
              <div className="gd-toolbar__left">
                <button
                  type="button"
                  className="gd-btn-circle"
                  onClick={closeDetail}
                  aria-label={t("discover.close_detail")}
                >
                  <Icon name="ri-close-line" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="gd-btn-circle"
                  onClick={closeDetail}
                  aria-label={t("activity.back")}
                >
                  <Icon name="ri-arrow-left-s-line" className="rtl:-scale-x-100" aria-hidden="true" />
                </button>
              </div>
              <div className="gd-toolbar__right">
                {secondaryActions}
                {bookLink("gd-btn-action gd-btn-action--primary")}
              </div>
            </div>

            <div className="gd-header">
              <div className="gd-meta">
                <span className="gd-tag">{item.subtitle}</span>
                {/* Not again when the line before already names the place
                    ("…in Gueliz, Marrakech"): it wrapped to a lone
                    "· Marrakech". */}
                {item.location &&
                !item.subtitle?.toLowerCase().includes(item.location.toLowerCase()) ? (
                  <span className="gd-tag">{item.location}</span>
                ) : null}
              </div>
              <h1 className="gd-title">{item.title}</h1>
              {/* Star ratings on stays only (owner rule). */}
              {item.rating && item.segment === "stays" && (
                <div className="gd-rating">
                  <Icon name="ri-star-fill" aria-hidden="true" />
                  <span className="gd-rating__score">{item.rating}</span>
                  {item.mentions > 0 && (
                    <span className="gd-rating__count">({item.mentions})</span>
                  )}
                </div>
              )}
            </div>

            <div className="gd-actions">{secondaryActions}</div>

            <div className="gd-gallery">
              <button
                type="button"
                className="gd-gallery__main gd-gallery__btn"
                onClick={() => heroSrc && setLightboxIndex(0)}
                disabled={!heroSrc}
                aria-label={heroSrc ? t("discover.gallery_open_image", { n: 1, total: galleryImages.length }) : t("discover.gallery_no_image")}
              >
                {heroSrc ? (
                  <>
                    <img
                      src={thumbUrl(heroSrc, 640)}
                      alt={galleryAlt(0)}
                      onError={() => markBroken(heroSrc)}
                    />
                    <span className="gd-gallery__zoom" aria-hidden="true">
                      <Icon name="ri-fullscreen-line" />
                    </span>
                  </>
                ) : (
                  <div className="gd-gallery__thumb-fallback" />
                )}
              </button>
              <button
                type="button"
                className="gd-gallery__thumb gd-gallery__btn"
                onClick={() => item.gallery[1] && !brokenSrcs.has(item.gallery[1]) && setLightboxIndex(1)}
                disabled={!item.gallery[1] || brokenSrcs.has(item.gallery[1])}
                aria-label={item.gallery[1] && !brokenSrcs.has(item.gallery[1]) ? t("discover.gallery_open_image", { n: 2, total: galleryImages.length }) : t("discover.gallery_no_image")}
              >
                {item.gallery[1] && !brokenSrcs.has(item.gallery[1]) ? (
                  <img
                    src={thumbUrl(item.gallery[1], 480)}
                    alt={galleryAlt(1)}
                    onError={() => markBroken(item.gallery[1]!)}
                  />
                ) : (
                  <div className="gd-gallery__thumb-fallback" />
                )}
              </button>
              <button
                type="button"
                className="gd-gallery__thumb gd-gallery__btn"
                onClick={() => item.gallery[2] && !brokenSrcs.has(item.gallery[2]) && setLightboxIndex(2)}
                disabled={!item.gallery[2] || brokenSrcs.has(item.gallery[2])}
                aria-label={item.gallery[2] && !brokenSrcs.has(item.gallery[2]) ? t("discover.gallery_open_image", { n: 3, total: galleryImages.length }) : t("discover.gallery_no_image")}
              >
                {item.gallery[2] && !brokenSrcs.has(item.gallery[2]) ? (
                  <>
                    <img
                      src={thumbUrl(item.gallery[2], 480)}
                      alt={galleryAlt(2)}
                      onError={() => markBroken(item.gallery[2]!)}
                    />
                    {galleryImages.length > 3 ? (
                      <span className="gd-gallery__more" aria-hidden="true">
                        {t("discover.gallery_more", { count: galleryImages.length - 3 })}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <div className="gd-gallery__thumb-fallback" />
                )}
              </button>
            </div>

            <div className="gd-tabs-block">
              <div className="gd-tabs" role="tablist">
                {DETAIL_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className={`gd-tab${activeTab === tab.id ? " is-active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {t(tab.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            <div className="gd-sections">
              {activeTab === "overview" && (
                <>
                  {(() => {
                    const headerHtml = item.headerDescription || item.description;
                    if (headerHtml && looksLikeHtml(headerHtml)) {
                      return (
                        <section className="gd-section">
                          <div className="gd-section__header">
                            <div className="gd-section__icon">
                              <Icon name="ri-compass-3-line" aria-hidden="true" />
                            </div>
                            <h4>{t("activity.overview")}</h4>
                          </div>
                          <div
                            className="gd-section__body gd-overview gd-html"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: headerHtml }}
                          />
                        </section>
                      );
                    }
                    const structured = casablancaListingSections[item.id];
                    const hasStructured = structured && structured.length > 0;
                    if (!hasStructured) {
                      const MdxBody = casablancaListingBodies[item.id];
                      return MdxBody ? (
                        <section className="gd-section">
                          <div className="gd-section__header">
                            <div className="gd-section__icon">
                              <Icon name="ri-compass-3-line" aria-hidden="true" />
                            </div>
                            <h4>{t("activity.overview")}</h4>
                          </div>
                          <div className="gd-section__body gd-mdx">
                            <MdxBody />
                          </div>
                        </section>
                      ) : null;
                    }
                    const paras = structured
                      .map((s) => s.body)
                      .join("\n\n")
                      .split(/\n{2,}/)
                      .map((p) => p.trim())
                      .filter(Boolean);
                    return (
                      <section className="gd-section">
                        <div className="gd-section__header">
                          <div className="gd-section__icon">
                            <Icon name="ri-compass-3-line" aria-hidden="true" />
                          </div>
                          <h4>{t("activity.overview")}</h4>
                        </div>
                        <div className="gd-section__body gd-overview">
                          {paras.map((p, i) => (
                            <p key={i}>{p}</p>
                          ))}
                        </div>
                      </section>
                    );
                  })()}

                  {hasAnyContentBlocks(item)
                    ? (
                      <section className="gd-section gd-section--blocks">
                        <DiscoverContentBlocks
                          item={item}
                          locale={pickLocale(lang)}
                        />
                      </section>
                    )
                    : item.overviewSections.map((section) => (
                        <section key={section.title} className="gd-section">
                          <div className="gd-section__header">
                            <div className="gd-section__icon">
                              <Icon name="ri-checkbox-circle-line" aria-hidden="true" />
                            </div>
                            <h4>{section.title}</h4>
                          </div>
                          <div className="gd-section__body">
                            <p>{section.body}</p>
                          </div>
                        </section>
                      ))}

                  {(() => {
                    if (hasAnyContentBlocks(item)) return null;
                    const blocks = buildGoodToKnow(item, itemPrice(item));
                    if (blocks.length === 0) return null;
                    return (
                      <section className="gd-section">
                        <div className="gd-section__header">
                          <div className="gd-section__icon">
                            <Icon name="ri-sparkling-line" aria-hidden="true" />
                          </div>
                          <h4>{t("activity.good_to_know")}</h4>
                        </div>
                        <div className="gd-section__body">
                          <div className="gd-info-grid">
                            {blocks.map((b) => (
                              <div key={`${b.label}-${b.value}`} className="gd-info-block">
                                <span className="gd-info-block__label">{t(b.label)}</span>
                                <strong className="gd-info-block__value">{b.value}</strong>
                              </div>
                            ))}
                          </div>
                        </div>
                      </section>
                    );
                  })()}

                  {item.questions.length > 0 && (
                    <section className="gd-section">
                      <div className="gd-section__header">
                        <div className="gd-section__icon">
                          <Icon name="ri-questionnaire-line" aria-hidden="true" />
                        </div>
                        <h4>{t("discover.people_also_ask")}</h4>
                      </div>
                      <div className="gd-section__body">
                        <ul className="gd-questions">
                          {item.questions.map((q) => (
                            <li key={q} className="gd-question">
                              <Icon name="ri-arrow-right-up-line" aria-hidden="true" />
                              <span>{q}</span>
                            </li>
                          ))}
                        </ul>
                        {item.faqs.length > 0 && (
                          <button
                            type="button"
                            className="gd-link-btn"
                            onClick={() => setActiveTab("faq")}
                          >
                            {t("discover.see_all_answers")}
                            <Icon name="ri-arrow-right-s-line" className="rtl:-scale-x-100" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </section>
                  )}

                  {item.availablePacks.length > 0 && (
                    <section className="gd-section">
                      <div className="gd-section__header">
                        <div className="gd-section__icon">
                          <Icon name="ri-ticket-2-line" aria-hidden="true" />
                        </div>
                        <h4>{t("discover.options")}</h4>
                      </div>
                      <div className="gd-section__body">
                        <ul className="gd-packs">
                          {item.availablePacks.map((pack) => (
                            <li key={pack.name} className="gd-pack">
                              <div className="gd-pack__body">
                                <strong>{pack.name}</strong>
                                {pack.timeLabel && <span>{pack.timeLabel}</span>}
                              </div>
                              <span className="gd-pack__price">{pack.priceLabel}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </section>
                  )}
                </>
              )}

              {activeTab === "location" && (
                <section className="gd-section">
                  <div className="gd-section__header">
                    <div className="gd-section__icon">
                      <Icon name="ri-map-pin-line" aria-hidden="true" />
                    </div>
                    <h4>{t("activity.location")}</h4>
                  </div>
                  <div className="gd-section__body">
                    <p>
                      <strong>{item.location}</strong>
                    </p>
                    {item.canShowLocation && item.latitude != null && item.longitude != null && (
                      <div className="gd-location-map">
                        <LocationMap
                          title={t("discover.map_for", { title: item.title })}
                          latitude={item.latitude}
                          longitude={item.longitude}
                          className="gd-location-map__map"
                        />
                      </div>
                    )}
                  </div>
                </section>
              )}

              {activeTab === "faq" && (
                <section className="gd-section">
                  <div className="gd-section__header">
                    <div className="gd-section__icon">
                      <Icon name="ri-question-line" aria-hidden="true" />
                    </div>
                    <h4>{t("discover.frequently_asked")}</h4>
                  </div>
                  <div className="gd-section__body">
                    {item.faqs.length > 0 ? (
                      <ul className="gd-faqs">
                        {item.faqs.map((faq) => (
                          <li key={faq.question} className="gd-faq">
                            <strong>{faq.question}</strong>
                            <p>{faq.answer}</p>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="gd-empty">{t("discover.faq_empty")}</p>
                    )}
                  </div>
                </section>
              )}
            </div>
          </div>
          {/* Phone: what it costs and how to book, floating over the page
              (owner, 2026-11-03: sheet B, pewter, floating). Explore places
              have no price and never say book. */}
          <div className="gd-bookbar">
            <div className="gd-bookbar__inner">
              <span className="gd-bookbar__price">
                <StopPrice item={item} className="gd-bookbar__amount" />
                {bookKind === "activity" ? (
                  <small>{t("activity.pay_on_arrival")}</small>
                ) : bookKind === "stay" ? (
                  <small>{t("activity.pay_by_link_title")}</small>
                ) : bookKind === "table" ? (
                  <small>{t("activity.table_promise_short")}</small>
                ) : null}
              </span>
              <button
                type="button"
                className={`gd-bookbar__trip${inActiveTrip ? " is-active" : ""}`}
                onClick={handleAddToTrip}
                disabled={inActiveTrip}
                aria-pressed={inActiveTrip}
              >
                <Icon name={inActiveTrip ? "ri-check-line" : "ri-add-line"} aria-hidden="true" />
                {addToTripLabel}
              </button>
              {bookLink("gd-bookbar__book")}
            </div>
          </div>
          {mounted &&
            lightboxIndex !== null &&
            galleryImages[lightboxIndex] &&
            createPortal(
            <div
              className="gd-lightbox"
              role="dialog"
              aria-modal="true"
              aria-label={t("discover.gallery_viewer", { title: item.title })}
              onClick={() => setLightboxIndex(null)}
            >
              <button
                type="button"
                className="gd-lightbox__close"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(null);
                }}
                aria-label={t("place.close_viewer")}
              >
                <Icon name="ri-close-line" aria-hidden="true" />
              </button>
              {galleryImages.length > 1 && (
                <button
                  type="button"
                  className="gd-lightbox__nav gd-lightbox__nav--prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(
                      (i) =>
                        i === null
                          ? null
                          : (i - 1 + galleryImages.length) %
                            galleryImages.length,
                    );
                  }}
                  aria-label={t("blog.previous_image")}
                >
                  <Icon name="ri-arrow-left-s-line" className="rtl:-scale-x-100" aria-hidden="true" />
                </button>
              )}
              <figure
                className="gd-lightbox__figure"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={thumbUrl(galleryImages[lightboxIndex], 1600)}
                  alt={galleryAlt(lightboxIndex)}
                />
                <figcaption>
                  <span>{galleryAlt(lightboxIndex)}</span>
                  <span className="gd-lightbox__count">
                    {lightboxIndex + 1} / {galleryImages.length}
                  </span>
                </figcaption>
              </figure>
              {galleryImages.length > 1 && (
                <button
                  type="button"
                  className="gd-lightbox__nav gd-lightbox__nav--next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex((i) =>
                      i === null ? null : (i + 1) % galleryImages.length,
                    );
                  }}
                  aria-label={t("blog.next_image")}
                >
                  <Icon name="ri-arrow-right-s-line" className="rtl:-scale-x-100" aria-hidden="true" />
                </button>
              )}
            </div>,
            document.body,
          )}
        </div>
      )}
    </aside>
  );
}
